import React, {useMemo} from "react"
import {selectUnit} from "@formatjs/intl-utils"
import {useIntl as _useIntl} from "react-intl"
import {IntlCtx} from "./types"
import {intlOverridesCtx} from "./Provider"

/**
 * Context exposing Intl imperative apis for displaying strings and numbers such as `formatMessage`
 *
 * NOTE: please prefer using `Formatted*` components exposed by `@irati/utils/lang` wherever possible
 *
 * @see [react-intl](https://formatjs.github.io/docs/react-intl/api#useintl-hook)
 */
export const useIntl = (): IntlCtx => {
  // NOTE: we strip down the exposed types api to help cut down on shenanigans
  const intl = _useIntl()
  const overrides = React.useContext(intlOverridesCtx)
  if (!overrides) throw new Error("intlOverridesCtx.Provider is missing from tree")

  return useMemo(
    () => ({
      ...intl,
      // we may require more overrides, but right now this is to allow web to
      // pass in a custom formatMessage provided by phrase for creating an in
      // app editor. See usages of `LangProvider` for details
      formatMessage: overrides.intlOverrides?.(intl).formatMessage ?? intl.formatMessage,
      formatRelative: (timestamp: string) => {
        const valueMillis = Number(new Date(timestamp))
        const diff = valueMillis - Date.now()
        const {value, unit} = selectUnit(Date.now() + diff)
        return intl.formatRelativeTime(value, unit)
      }
    }),
    [intl, overrides]
  )
}
